import React, { useState, useEffect, forwardRef, useImperativeHandle, useContext } from 'react'
import PropTypes from 'prop-types'
import { EmailRevalidateTemplate } from '../../templates/EmailRevalidateTemplate'
import { useForm } from 'react-hook-form'
import { handleEmailValidationExceptions } from './exceptions/emailExceptions'
import { RevalidationContext } from '../../../util/providers/RevalidationProvider'
import { confirmationSendPinCode, emailChangeSendPinCode } from '../../../api/pinCodeApi'
import { isValidEmail } from '../../../util/validators'

export const SendEmailRevalidationPinCodePage = forwardRef(({ onSuccess, onBack }, ref) => {
  const { email, updateValidations, token, updateSendEmailRequestId, oldEmail, lurker } =
    useContext(RevalidationContext)

  const [alreadyHasEmail, setAlreadyHasEmail] = useState(!!email.value)
  const [errorAlert, setErrorAlert] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { control, formState, handleSubmit, setValue, reset, clearErrors } = useForm({
    defaultValues: { email: email.value ?? '' },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const shouldDisableSendButton = !formState.isValid || isSubmitting

  useEffect(() => {
    lurker.pageLoadEvt('revalidate_email_send')

    oldEmail && reset({ email: oldEmail })
  }, [])

  const handleBack = () => {
    if (alreadyHasEmail || (!email.value && !alreadyHasEmail)) {
      onBack && onBack()
    }

    setAlreadyHasEmail(!!email.value)
    setErrorAlert(null)
    reset({ email: email.value })
  }

  const handleClickFooterButton = () => {
    lurker.clickEvt('change_email')

    setAlreadyHasEmail(false)
    setErrorAlert(null)
    setIsSubmitting(false)
    reset({ email: '' })
  }

  const onMailCheckSuggestionClick = suggestion => {
    setValue('email', suggestion, { shouldValidate: true })
  }

  const onValidateEmailSuccess = emailValue => {
    lurker.viewMessageEvt('success')

    setIsSubmitting(false)

    if (onSuccess) {
      updateValidations('email', emailValue)
      onSuccess()
    }
  }

  useImperativeHandle(ref, () => ({
    handleBack,
  }))

  const onValidateEmailError = (error = {}) => {
    const { errorInfo, lurkerExtraData } = handleEmailValidationExceptions(error)

    lurker.errorMessageEvt('error', { ...lurkerExtraData })

    setErrorAlert(errorInfo)
  }

  const validateEmail = email => (alreadyHasEmail ? email : isValidEmail(email) ? email : null)

  const onSubmit = ({ email }) => {
    const newEmail = !alreadyHasEmail ? validateEmail(email) : undefined

    lurker.clickEvt('submit')

    setIsSubmitting(true)

    const sendPromise = alreadyHasEmail
      ? confirmationSendPinCode(newEmail, 'EMAIL', token)
      : emailChangeSendPinCode(newEmail, token)

    const viewMessageEvtSent = alreadyHasEmail ? 'pincode_email_sent' : 'pincode_changed_email_sent'
    const viewMessageEvtError = alreadyHasEmail
      ? 'pincode_email_send_error'
      : 'pincode_changed_email_send_error'

    sendPromise
      .then(({ data }) => {
        lurker.viewMessageEvt(viewMessageEvtSent, { resend: false })
        updateSendEmailRequestId(data?.requestId)
        onValidateEmailSuccess(email)
      })
      .catch(error => {
        lurker.viewMessageEvt(viewMessageEvtError, { resend: false })

        onValidateEmailError(error)
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <EmailRevalidateTemplate
      error={errorAlert}
      shouldDisableSendButton={shouldDisableSendButton}
      isSubmitting={isSubmitting}
      onMailCheckSuggestionClick={onMailCheckSuggestionClick}
      alreadyHasEmail={alreadyHasEmail}
      inputControl={control}
      onBack={handleBack}
      onSubmit={handleSubmit(onSubmit)}
      onClickFooterButton={handleClickFooterButton}
      clearErrors={() => clearErrors('email')}
    />
  )
})

SendEmailRevalidationPinCodePage.displayName = 'SendEmailRevalidationPinCodePage'

SendEmailRevalidationPinCodePage.propTypes = {
  onBack: PropTypes.func,
  onSuccess: PropTypes.func,
}
